/**
 * @typedef {Object} EventBus
 * @property {Object} EVENTS - The event constants.
 * @property {Object} debug - Debug utilities for the event bus.
 * @property {Function} publish - Publishes an event.
 * @property {Function} subscribe - Subscribes to an event.
 * @property {Function} unsubscribe - Unsubscribes from an event.
 */

const {
  createEventEmitter,
  createSyncEventEmitter,
} = require('./eventEmitters');

/**
 * Creates an event bus for managing custom events and subscriptions.
 * @param {string} namespace - The namespace for the event bus.
 * @param {Object} events - The event configuration object.
 * @param {Object} events.events - Custom event names.
 * @param {Object} events.payloads - Event payloads.
 * @returns {EventBus} The event bus API with
 * EVENTS, debug, publish, subscribe, and unsubscribe.
 */
function createEventBus(namespace, { events = {}, payloads = {} }) {
  /**
   * Events for collecting information
   * @readonly
   * @enum {string}
   */
  const EVENTBUS_EVENTS = {
    ...events,
    PAYLOADS: payloads,
  };

  // Assure a single broker for every js which calls this function.
  const broker = createEventEmitter();
  const syncBroker = createSyncEventEmitter();

  const eventNames = Object.values(events);
  const exists = (eventName) => eventNames.includes(eventName);

  /**
   * Publish an event
   * @param {EVENTBUS_EVENTS} eventName The event to publish to
   * @param {*} [data] The payload that is useful for event subscribers
   */
  function publish(eventName, data) {
    if (!exists(eventName)) {
      throw new TypeError(`Unknown ${namespace} event: '${eventName}'`);
    }
    syncBroker.emit(eventName, data).then(() => {
      broker.emit(eventName, data);
    });
  }

  /**
   * Subscribe to an event
   * @param {EVENTBUS_EVENTS} eventName The event subscribed
   * @param {any} handler The handler of the subscription,
   * which receives an event
   * @param {Object} [options] Optional params
   * @param {boolean} [options.synchronous=false] The handler will be called
   * synchronously and before the async handlers
   */
  function subscribe(eventName, handler, { synchronous = false } = {}) {
    if (Array.isArray(eventName)) {
      eventName.forEach((singleEventName) =>
        subscribe(singleEventName, handler)
      );
      return;
    }

    if (!exists(eventName)) {
      throw new TypeError(`Unknown ${namespace} event: '${eventName}'`);
    }

    if (synchronous === true) {
      syncBroker.on(eventName, handler);
    } else {
      broker.on(eventName, handler);
    }
  }

  /**
   * Unsubscribe from an event
   * @param {EVENTBUS_EVENTS} eventName The event unsubscribed
   * @param {any} handler The handler of the unsubscription,
   * which receives an event
   */
  function unsubscribe(eventName, handler) {
    if (Array.isArray(eventName)) {
      eventName.forEach((singleEventName) =>
        unsubscribe(singleEventName, handler)
      );
      return;
    }

    if (!exists(eventName)) {
      throw new TypeError(`Unknown ${namespace} event: '${eventName}'`);
    }
    syncBroker.off(eventName, handler);
    broker.off(eventName, handler);
  }

  const debug = {
    mustShowMoreInfo: false,
    /* eslint-disable no-console */
    print: (eventName, payload) => {
      console.group(`[byl/${namespace}] "${eventName}"`);
      console.log('payload:', payload);
      if (debug.mustShowMoreInfo) {
        console.trace();
      }
      console.groupEnd();
    },
    on(mustShowMoreInfo) {
      debug.mustShowMoreInfo = mustShowMoreInfo ?? false;
      const callback = debug.print;
      broker.on('*', callback);
      console.log(`[byl/${namespace}] DEBUG ON`);
    },
    /* eslint-enable no-console */
  };

  return {
    EVENTS: EVENTBUS_EVENTS,
    debug,
    publish,
    subscribe,
    unsubscribe,
  };
}

module.exports = {
  createEventBus,
};
